export type BemobiCheckoutErrorPhase = 'load' | 'payment';

const BEMOBI_SESSION_LOAD_ERROR_CODES = new Set([
  'read-session-failed',
  'checkout-unauthorized',
  'checkout/unauthorized'
]);

const BEMOBI_ERROR_MESSAGES: Record<string, string> = {
  'read-session-failed':
    'Não foi possível iniciar o pagamento. A sessão expirou ou é inválida. Tente finalizar a compra novamente.',
  'checkout-unauthorized':
    'Não foi possível iniciar o pagamento. Sessão não autorizada. Tente finalizar a compra novamente.',
  'checkout/unauthorized':
    'Não foi possível iniciar o pagamento. Sessão não autorizada. Tente finalizar a compra novamente.',
  'internal-server-error':
    'Não foi possível carregar o checkout no momento. Tente novamente em instantes.',
  'payment-timeout': 'O pagamento demorou para responder. Tente novamente.'
};

export const BEMOBI_LOAD_ERROR_FALLBACK =
  'Não foi possível carregar o checkout. Tente novamente.';

export const BEMOBI_PAYMENT_ERROR_FALLBACK =
  'Não foi possível concluir o pagamento. Tente novamente.';

export function extractBemobiErrorCode(source: unknown): string | null {
  if (source === null || typeof source !== 'object') {
    return null;
  }

  const record = source as Record<string, unknown>;
  const nestedExtraCode = extractNestedPostMessageType(record);

  if (nestedExtraCode !== null) {
    return nestedExtraCode;
  }

  const type = record['type'];

  if (typeof type === 'string' && !isStandardCheckoutMessageType(type)) {
    return type;
  }

  const payload = record['payload'];

  if (payload !== undefined) {
    return extractBemobiErrorCode(payload);
  }

  return null;
}

export function isBemobiSessionLoadError(
  errorCode: string | null | undefined
): boolean {
  if (errorCode === null || errorCode === undefined) {
    return false;
  }

  return BEMOBI_SESSION_LOAD_ERROR_CODES.has(errorCode);
}

export function resolveBemobiCheckoutErrorMessage(
  payload: unknown,
  errorCode: string | null | undefined,
  phase: BemobiCheckoutErrorPhase
): string {
  const resolvedCode = errorCode ?? extractBemobiErrorCode(payload);

  if (
    resolvedCode !== null &&
    BEMOBI_ERROR_MESSAGES[resolvedCode] !== undefined
  ) {
    return BEMOBI_ERROR_MESSAGES[resolvedCode];
  }

  if (phase === 'load') {
    return BEMOBI_LOAD_ERROR_FALLBACK;
  }

  return BEMOBI_PAYMENT_ERROR_FALLBACK;
}

function extractNestedPostMessageType(
  record: Record<string, unknown>
): string | null {
  const extra = record['extra'];

  if (typeof extra !== 'object' || extra === null) {
    return null;
  }

  const postMessage = (extra as Record<string, unknown>)['postMessage'];

  if (typeof postMessage !== 'object' || postMessage === null) {
    return null;
  }

  const nestedType = (postMessage as Record<string, unknown>)['type'];

  return typeof nestedType === 'string' ? nestedType : null;
}

function isStandardCheckoutMessageType(type: string): boolean {
  return type.startsWith('checkout:');
}
